import * as uuid from 'uuid';
import { getCookieChatbot, setCookieByHoursChatbot } from './cookiesUtils.js';

// Cookie names
const UUID_COOKIE_NAME = 'chatbot_uuid';
const SESSION_INDEX_COOKIE_NAME = 'chatbot_session_index';

/**
 * Generates or retrieves a UUID for the chatbot session.
 * This function ensures the same UUID is shared across all tabs in the same browser.
 * Uses cookies with 2-hour expiration for both UUID and session index.
 * When conversation history is disabled, always generates a fresh UUID without persistence.
 * 
 * @param {string|null} uuidString - Existing UUID string to increment index
 * @param {number} index - Index to append to the UUID
 * @returns {string} The UUID string with format "uuid - index"
 */
export const generateUuidChatbot = (uuidString = null, index = 0) => {
  const historyMode = import.meta.env.VITE_CONVERSATION_HISTORY_MODE || 'enabled';
  
  // If history is disabled, always generate fresh UUID (no cookie persistence)
  if (historyMode === 'disabled') {
    const newUuid = uuid.v7();
    const dst = `${newUuid} - ${index}`;
    // Don't save to cookies - always fresh UUID
    return dst;
  }
  
  // If we have an existing uuidString, increment its index
  if (uuidString) {
    const uuidRaw = uuidString.replace(/ - \d+$/, '');
    const dst = uuidRaw + ` - ${index}`;
    
    // Save base UUID and session index in cookies with 2-hour expiration
    setCookieByHoursChatbot(UUID_COOKIE_NAME, uuidRaw, 2);
    setCookieByHoursChatbot(SESSION_INDEX_COOKIE_NAME, index.toString(), 2);
    
    return dst;
  }
  
  // Check if there's already a UUID in cookies (shared across tabs)
  const existingUuid = getExistingUuidChatbot();
  if (existingUuid) {
    // Use existing UUID but with the provided index
    const uuidRaw = existingUuid.replace(/ - \d+$/, '');
    const dst = uuidRaw + ` - ${index}`;
    
    // Update session index cookie with new index
    setCookieByHoursChatbot(SESSION_INDEX_COOKIE_NAME, index.toString(), 2);
    
    return dst;
  }
  
  // Generate new UUID if none exists
  const newUuid = uuid.v7();
  const dst = `${newUuid} - ${index}`;
  
  // Save base UUID and session index in cookies with 2-hour expiration
  setCookieByHoursChatbot(UUID_COOKIE_NAME, newUuid, 2);
  setCookieByHoursChatbot(SESSION_INDEX_COOKIE_NAME, index.toString(), 2);
  
  return dst;
}

/**
 * Retrieves existing UUID from cookies
 * @returns {string|null} Existing UUID or null if not found
 */
export const getExistingUuidChatbot = () => {
  try {
    const baseUuid = getCookieChatbot(UUID_COOKIE_NAME);
    const sessionIndex = getCookieChatbot(SESSION_INDEX_COOKIE_NAME);
    
    if (baseUuid && sessionIndex) {
      return `${baseUuid} - ${sessionIndex}`;
    } else if (baseUuid) {
      // If only base UUID exists, use index 0
      return `${baseUuid} - 0`;
    }
    
    return null;
  } catch (error) {
    console.warn('Could not access cookies for UUID:', error);
    return null;
  }
}

/**
 * Gets the base UUID without the index suffix
 * @param {string} uuidString - Full UUID string with index
 * @returns {string} Base UUID without index
 */
export const getBaseUuidChatbot = (uuidString) => {
  return uuidString.replace(/ - \d+$/, '');
}

/**
 * Gets the session index from a UUID string
 * @param {string} uuidString - Full UUID string with index
 * @returns {string} Session index
 */
export const getSessionIndexChatbot = (uuidString) => {
  const parts = uuidString.split(' - ');
  return parts.length > 1 ? parts[1] : '0';
}

/**
 * Updates the session index in cookies
 * @param {string} newIndex - New session index
 */
export const updateSessionIndexChatbot = (newIndex) => {
  setCookieByHoursChatbot(SESSION_INDEX_COOKIE_NAME, newIndex.toString(), 2);
}

/**
 * Gets the current session index from cookies
 * @returns {string} Current session index or '0' if not found
 */
export const getCurrentSessionIndexChatbot = () => {
  return getCookieChatbot(SESSION_INDEX_COOKIE_NAME) || '0';
}

/**
 * Removes UUID and session index cookies
 */
export const removeUuidChatbot = () => {
  try {
    // Set cookies to expire immediately
    setCookieByHoursChatbot(UUID_COOKIE_NAME, '', 0);
    setCookieByHoursChatbot(SESSION_INDEX_COOKIE_NAME, '', 0);
  } catch (error) {
    console.warn('Could not remove UUID cookies:', error);
  }
}

/**
 * Checks if UUID cookies are expired or will expire soon
 * @returns {boolean} True if cookies are expired or will expire within 5 minutes
 */
export const isUuidExpiredChatbot = () => {
  try {
    const baseUuid = getCookieChatbot(UUID_COOKIE_NAME);
    const sessionIndex = getCookieChatbot(SESSION_INDEX_COOKIE_NAME);
    
    // If either cookie is missing, consider it expired
    if (!baseUuid || !sessionIndex) {
      return true;
    }
    
    // Check if cookies will expire within 5 minutes
    const now = new Date();
    const cookieExpiry = new Date(now.getTime() + (2 * 60 * 60 * 1000)); // 2 hours from now
    const fiveMinutesFromNow = new Date(now.getTime() + (5 * 60 * 1000));
    
    return cookieExpiry <= fiveMinutesFromNow;
  } catch (error) {
    console.warn('Could not check UUID expiration:', error);
    return true;
  }
}
